import { lazy, Suspense, useMemo } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  generatePath,
  useParams,
  useLocation,
  type RouteObject,
} from "react-router-dom";
import { Layout } from "./components/Layout";
import { AuthGuard } from "./components/AuthGuard";
import { Skeleton } from "@cloch-ai/ui";
import { useUnifiedShell } from "./hooks/useUnifiedShell";
import { useUnifiedCatalog } from "./hooks/useUnifiedCatalog";
import { useIsPlatformStaff } from "./hooks/useIsPlatformStaff";
import { useIsOrgAdmin } from "./hooks/useIsOrgAdmin";
import { useOrgContext } from "./providers/OrgProvider";
import { isSynapsedxConsoleEnabled } from "./lib/synapsedx-console";

/**
 * Permanent redirect that carries route params AND the query string + hash
 * across (e.g. /admin/agents/:id → /workspace/agents/:id, or
 * /marketplace?contract=c-42 → /routines?contract=c-42). React Router's
 * <Navigate> interpolates neither params nor search, and the re-homed pages
 * still consume both — the marketplace contract deep link (SYN-1490) opens a
 * modal from `?contract=`, and quick-start still emits those old URLs — so we
 * resolve params and forward search/hash here. generatePath is a no-op for
 * param-less targets, so this one helper covers every re-homed redirect.
 */
export function KeepSearchRedirect({ to }: { to: string }) {
  const params = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`${generatePath(to, params)}${search}${hash}`} replace />;
}

/**
 * /finops redirect under the unified shell (spec §6.2): org admins/owners land
 * on Billing; members land on Home. Query/hash forwarded for parity with the
 * other re-homed redirects.
 */
function FinOpsRedirect() {
  const isOrgAdmin = useIsOrgAdmin();
  const { search, hash } = useLocation();
  const target = isOrgAdmin ? "/workspace/billing" : "/home";
  return <Navigate to={`${target}${search}${hash}`} replace />;
}

/**
 * /workspace/billing gate (SYN-1523, spec §8.5): members never see Billing —
 * a non-admin landing here is redirected to /home (their credit visibility is
 * the Home snapshot). Mirrors FinOpsRedirect's role check.
 */
export function BillingAdminGate({ children }: { children: React.ReactNode }) {
  const isOrgAdmin = useIsOrgAdmin();
  if (!isOrgAdmin) {
    return <Navigate to="/home" replace />;
  }
  return <>{children}</>;
}

// Lazy load pages
const PublisherExport = lazy(() => import("./pages/publisher-export"));
const PublisherPreview = lazy(() => import("./pages/publisher-preview"));
const Chat = lazy(() => import("./pages/chat-v2"));
const Home = lazy(() => import("./pages/home"));
const Onboarding = lazy(() => import("./pages/onboarding"));
const SignIn = lazy(() => import("./pages/sign-in"));
const SignUp = lazy(() => import("./pages/sign-up"));
const DocumentView = lazy(() => import("./pages/document-view"));
const SsoCallback = lazy(() => import("./pages/sso-callback"));
const AcceptInvitation = lazy(() => import("./pages/accept-invitation"));
const Pricing = lazy(() => import("./pages/pricing"));
const Start = lazy(() => import("./pages/start"));
const CheckoutSuccess = lazy(() => import("./pages/checkout-success"));
const MeAgents = lazy(() => import("./pages/me/agents"));
const MeAgentEdit = lazy(() => import("./pages/me/agent-edit"));
const UserSettings = lazy(() => import("./pages/settings"));
const FinOps = lazy(() => import("./pages/finops"));
const Marketplace = lazy(() => import("./pages/marketplace"));
const Customize = lazy(() => import("./pages/customize"));
const AdminAgents = lazy(() => import("./pages/admin/agents"));
const AdminAgentEdit = lazy(() => import("./pages/admin/agent-edit"));
const AdminChannels = lazy(() => import("./pages/admin/channels"));
const AdminOverview = lazy(() => import("./pages/admin/overview"));
const AdminModels = lazy(() => import("./pages/admin/models"));
const AdminSkills = lazy(() => import("./pages/admin/skills"));
const AdminUsage = lazy(() => import("./pages/admin/usage"));
const AdminRoles = lazy(() => import("./pages/admin/roles"));
const AdminConnectors = lazy(() => import("./pages/admin/connectors"));
// Unified-shell dedicated pages (SYN-1506): /routines list + detail + run ceremony.
// NOTE: The /routines list page is superseded by /catalog only when
// ui.unified_catalog is ON (SYN-1948 WS2). When the catalog flag is OFF the
// list page must still mount so flag-off behaviour stays byte-identical, so the
// lazy import is retained and gated at render time (see RoutinesListOrRedirect).
const Routines = lazy(() => import("./pages/routines"));
const RoutineDetail = lazy(() => import("./pages/routines/detail"));
// Story 027.007: one saved Routine.v1 routine, opened by the person's Use id.
const PersonalRoutineDetail = lazy(() => import("./pages/routines/PersonalRoutineDetail"));
// Unified catalog (SYN-1948, WS2): four-facet surface behind ui.unified_catalog.
const Catalog = lazy(() => import("./pages/catalog"));
// Contract-grade run ceremony (SYN-1512, spec §7.5).
const RoutineRunCeremony = lazy(() => import("./pages/routines/run"));
// Activity (SYN-1516): run history list + linkable run detail replace the
// ActivityPlaceholder stub.
const Activity = lazy(() => import("./pages/activity"));
const RunDetail = lazy(() => import("./pages/activity/detail"));
// Approvals (SYN-1521, spec §8.6): the unified three-source inbox — access
// requests, run confirmations, and agent escalations in one list.
const Approvals = lazy(() => import("./pages/approvals"));
// People (SYN-1509, spec §8.1): members table + invites with role pre-pick —
// the last SYN-1503 placeholder graduates to a dedicated page.
const People = lazy(() => import("./pages/people"));
// Connections (SYN-1520, spec §8.3): connected-system cards, auth health,
// add-connection wizard, and the migrated Channels tab.
const WorkspaceConnections = lazy(() => import("./pages/workspace/connections"));
// Database connections (SYN-2207, epic-005; 4 Oct 2026): connect a database, then ask
// questions of it. There is no review step any more: the structure is read live.
// Separate pages, deliberately NOT inline in chat-v2.tsx.
const DatabaseConnections = lazy(() => import("./pages/connections"));
const DatabaseConnect = lazy(() => import("./pages/connections/connect"));
const ChooseConnectionKind = lazy(() => import("./pages/connections/choose-kind"));
const DatabaseAsk = lazy(() => import("./pages/connections/ask"));

/** `/connections/:id` was the table review, which is gone (4 Oct 2026); its link now asks. */
function DatabaseConnectionHome() {
  const { id = "" } = useParams();
  return <Navigate to={`/connections/${encodeURIComponent(id)}/ask`} replace />;
}
const WorkspaceGovernance = lazy(() => import("./pages/workspace/governance"));
const WorkspaceSkills = lazy(() => import("./pages/workspace/skills"));
// D11 M1: apps offered to the org, enabled by its admin.
const WorkspaceApps = lazy(() => import("./pages/workspace/apps"));
// Roles workspace (SYN-1517, spec §8.2): role index cards + the role detail
// with the add-routines picker and permission impact preview. Replaces the
// re-homed /admin/roles page under the unified shell (the legacy table keeps
// AdminRoles untouched).
const WorkspaceRolesIndex = lazy(() => import("./pages/workspace/roles"));
const WorkspaceRoleDetail = lazy(() => import("./pages/workspace/roles/detail"));
const ClaudeCodeSetup = lazy(() => import("./pages/setup/claude-code"));
// SynapseDx console catalog (SYN-1953, WS5): everything-view + publish/unpublish.
const SynapseDxCatalog = lazy(() => import("./pages/synapsedx/catalog"));
// My Setup personal hub (SYN-1901): personal agents + sign-ins + profile.
const MySetup = lazy(() => import("./pages/my-setup"));

function PageLoader() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
}

export function SynapseDxStaffGate({ children }: { children: React.ReactNode }) {
  const { isLoading } = useOrgContext();
  const isPlatformStaff = useIsPlatformStaff();
  if (isLoading) {
    return <PageLoader />;
  }
  if (!isPlatformStaff) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

/**
 * /synapsedx/catalog route gate (SYN-1953): staff guard AND the console flag
 * (synapsedx.console.enabled / VITE_SYNAPSEDX_CONSOLE). The flag is fail-closed,
 * so a disabled/staged build redirects even a staff user who opens the route
 * directly — the layout hides the nav item, but the route must not render the
 * BFF UI when the flag is off. DATA is still gated by the server grant.
 */
export function SynapseDxConsoleGate({ children }: { children: React.ReactNode }) {
  if (!isSynapsedxConsoleEnabled()) {
    return <Navigate to="/" replace />;
  }
  return <SynapseDxStaffGate>{children}</SynapseDxStaffGate>;
}

/**
 * Shared wrapper for every protected route in BOTH the legacy and unified
 * route tables. No onboarding gate: everything published to the global
 * marketplace is available to every org from the moment they sign in, so
 * nothing here redirects to /onboarding — it stays a route reachable only by
 * explicit navigation (chat-first landing, no hard redirect).
 */
function ProtectedPage({ children }: { children: React.ReactNode }) {
  return (
    <AuthGuard>
      <Layout>
        <Suspense fallback={<PageLoader />}>{children}</Suspense>
      </Layout>
    </AuthGuard>
  );
}

function PublicPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

/**
 * /routines list route under the unified shell (SYN-1948 WS2).
 *
 * The catalog (`ui.unified_catalog`) and shell (`ui.unified_shell`) flags are
 * independent and org-scoped. The router table is selected by the shell flag
 * only, so this element must gate on the catalog flag at render time:
 *   - catalog ON  → redirect to /catalog (Run collapses into Catalog, AC2/AC3)
 *   - catalog OFF → mount the legacy Routines list (flag-off byte-identical)
 * Detail and run-ceremony sub-paths keep their own page routes regardless.
 */
function RoutinesListOrRedirect() {
  const unifiedCatalog = useUnifiedCatalog();
  if (unifiedCatalog) {
    return <KeepSearchRedirect to="/catalog" />;
  }
  return (
    <ProtectedPage>
      <Routines />
    </ProtectedPage>
  );
}

/**
 * Workspace-browse / marketplace redirects under the unified shell (SYN-1948
 * WS2). These halves consolidate into /catalog only when the catalog flag is
 * ON. With the flag OFF the pre-catalog target (/routines) is preserved so the
 * flag-off path is byte-identical to the shell-only table.
 */
function CatalogBrowseRedirect() {
  const unifiedCatalog = useUnifiedCatalog();
  return <KeepSearchRedirect to={unifiedCatalog ? "/catalog" : "/routines"} />;
}

/**
 * `/sign-up` — a REAL sign-up surface as of SYN-2250. It used to redirect to `/sign-in`,
 * which meant a buyer who had just paid and had no account had nowhere to go, because
 * `/sign-in` cannot create one. She was modelled as an invitee to her own company.
 *
 * That matters because Clerk appends `__clerk_ticket` to whichever URL it routes an
 * invitation to, and a ticket landing here was destroyed on arrival — the buyer got the
 * generic login card with no mention of the invitation or the plan she paid for. Tickets
 * belong at /accept, which knows how to redeem them; everything else keeps going to
 * sign-in, now with its query intact.
 *
 * ⇒ The real answer is a sign-up surface for someone who has PAID and has no account yet.
 *    That needs Atlas to resolve a paid intent from a checkout session rather than only
 *    from an invited user's Clerk metadata. Filed as SYN-2250.
 */
export function signUpRedirectTarget(search: string, hash = ""): string {
  const hasTicket = new URLSearchParams(search).has("__clerk_ticket");
  // A ticket still belongs at /accept, which knows how to redeem it — invitations remain the
  // right model for TEAMMATES she invites. Everyone else now stays on /sign-up and creates
  // their own account, carrying the paid context in the query.
  return `${hasTicket ? "/accept" : "/sign-up"}${search}${hash}`;
}

function SignUpRoute() {
  const { search, hash } = useLocation();
  if (new URLSearchParams(search).has("__clerk_ticket")) {
    return <Navigate to={signUpRedirectTarget(search, hash)} replace />;
  }
  return <SignUp />;
}

/**
 * Auth + public routes — identical in both IAs.
 */
const authRoutes: RouteObject[] = [
  {
    path: "/connections/oauth/return",
    element: (
      <ProtectedPage>
        <Chat />
      </ProtectedPage>
    ),
  },
  {
    path: "/sign-in",
    element: (
      <PublicPage>
        <SignIn />
      </PublicPage>
    ),
  },
  {
    path: "/sign-in/*",
    element: (
      <PublicPage>
        <SignIn />
      </PublicPage>
    ),
  },
  {
    path: "/sign-up",
    element: (
      <PublicPage>
        <SignUpRoute />
      </PublicPage>
    ),
  },
  {
    path: "/sign-up/*",
    element: (
      <PublicPage>
        <SignUpRoute />
      </PublicPage>
    ),
  },
  {
    path: "/sso-callback",
    element: (
      <PublicPage>
        <SsoCallback />
      </PublicPage>
    ),
  },
  {
    path: "/accept",
    element: (
      <PublicPage>
        <AcceptInvitation />
      </PublicPage>
    ),
  },
  {
    path: "/accept/*",
    element: (
      <PublicPage>
        <AcceptInvitation />
      </PublicPage>
    ),
  },
];

/**
 * The SMB buying surface — SYN-1388, epic-007 (beats ⓪ ① ② ②b ②c).
 *
 * 🔴 DEFINED ONCE AND SPREAD INTO **BOTH** TABLES, for the reason epic-005 recorded the
 * hard way: cloch runs `legacyRoutes` (`ui.unified_shell` OFF), so a route added only to
 * `unifiedRoutes` 404s in the browser while tsc, the route tests and the bundle are all
 * green. A feature whose reachability depends on a flag it has no relationship to is a
 * feature that is off for whoever has the flag off.
 *
 * PUBLIC on purpose. She has not paid yet, has no org, and is not signed in — the whole
 * point of beat ⓪ is that she arrives from somebody else's recommendation and can see
 * the price without meeting us (Principle 1).
 */
const smbBuyingRoutes: RouteObject[] = [
  {
    path: "/pricing",
    element: (
      <PublicPage>
        <Pricing />
      </PublicPage>
    ),
  },
  {
    // 🔴 Without this the primary paid CTA rendered a BLANK SCREEN: /pricing
    // navigates here and the SPA has no catch-all route. Caught by review.
    path: "/start",
    element: (
      <PublicPage>
        <Start />
      </PublicPage>
    ),
  },
  {
    // 🔴 Same trap, one step later and worse: Stripe returns a PAYING buyer to this
    // URL. Without the route she lands on a blank screen having just been charged.
    // PUBLIC because she has paid but not yet claimed an account — requiring sign-in
    // here would lock her out of the confirmation for the money she just spent.
    path: "/checkout/success",
    element: (
      <PublicPage>
        <CheckoutSuccess />
      </PublicPage>
    ),
  },
];

/**
 * Database connections — SYN-2207, epic-005 (moments 2, 3 and 4).
 *
 * 🔴 DEFINED ONCE AND SPREAD INTO **BOTH** TABLES, deliberately.
 *
 * These went into `unifiedRoutes` only on the first attempt, and the deployed cloch
 * instance runs `legacyRoutes` (`ui.unified_shell` OFF), so every page 404'd in the
 * browser while `tsc`, the route tests and the built bundle were all green. The chunks
 * were in the bundle; nothing routed to them. A feature whose reachability depends on a
 * flag it has no relationship to is a feature that is off for whoever has the flag off.
 *
 * Namespaced under `/connections` so it cannot be confused with `/workspace/connections`,
 * which is the SYN-1520 org-admin surface and a different thing.
 */
const databaseConnectionRoutes: RouteObject[] = [
  {
    // The resumable door (005.044). MUST come before /connections/:id or the
    // param route swallows it.
    path: "/connections",
    element: (
      <ProtectedPage>
        <DatabaseConnections />
      </ProtectedPage>
    ),
  },
  {
    // 🔴 KIND FIRST (SYN-2251). This used to mount the database form directly, which made
    // "database" look like the only thing a business is made of. The chooser names kind,
    // authentication and authorisation for every kind — including the ones we cannot do
    // yet — so the model is visible instead of implied by whatever page happened to open.
    path: "/connections/new",
    element: (
      <ProtectedPage>
        <ChooseConnectionKind />
      </ProtectedPage>
    ),
  },
  {
    path: "/connections/new/database",
    element: (
      <ProtectedPage>
        <DatabaseConnect />
      </ProtectedPage>
    ),
  },
  {
    path: "/connections/:id",
    element: (
      <ProtectedPage>
        <DatabaseConnectionHome />
      </ProtectedPage>
    ),
  },
  {
    path: "/connections/:id/ask",
    element: (
      <ProtectedPage>
        <DatabaseAsk />
      </ProtectedPage>
    ),
  },
];

/**
 * Legacy IA route table (flag OFF). This is byte-for-byte today's table — the
 * `ui.unified_shell` flag must produce zero change when off — plus the epic-005
 * journey above, which is new and must be reachable on both tables.
 */
export const legacyRoutes: RouteObject[] = [
  ...authRoutes,
  ...smbBuyingRoutes,
  ...databaseConnectionRoutes,

  // Main app
  { path: "/", element: <Navigate to="/chat" replace /> },
  {
    // SYN-2287 slice 5: what a citation's link opens. Authenticated, org-scoped server-side.
    path: "/documents/:id",
    element: (
      <AuthGuard>
        <DocumentView />
      </AuthGuard>
    ),
  },
  {
    path: "/publisher/export",
    element: (
      <ProtectedPage>
        <PublisherExport />
      </ProtectedPage>
    ),
  },
  {
    path: "/publisher/preview",
    element: (
      <ProtectedPage>
        <PublisherPreview />
      </ProtectedPage>
    ),
  },
  {
    path: "/chat",
    element: (
      <ProtectedPage>
        <Chat />
      </ProtectedPage>
    ),
  },
  {
    path: "/marketplace",
    element: (
      <ProtectedPage>
        <Marketplace />
      </ProtectedPage>
    ),
  },
  {
    path: "/customize",
    element: (
      <ProtectedPage>
        <Customize />
      </ProtectedPage>
    ),
  },
  {
    path: "/customize/:section",
    element: (
      <ProtectedPage>
        <Customize />
      </ProtectedPage>
    ),
  },
  {
    path: "/customize/packages/:packageId",
    element: (
      <ProtectedPage>
        <Customize />
      </ProtectedPage>
    ),
  },
  {
    path: "/customize/directory",
    element: (
      <ProtectedPage>
        <Customize />
      </ProtectedPage>
    ),
  },
  {
    path: "/finops",
    element: (
      <ProtectedPage>
        <FinOps />
      </ProtectedPage>
    ),
  },

  // User settings
  {
    path: "/settings",
    element: (
      <ProtectedPage>
        <UserSettings />
      </ProtectedPage>
    ),
  },
  {
    path: "/setup/claude-code",
    element: (
      <ProtectedPage>
        <SynapseDxStaffGate>
          <ClaudeCodeSetup />
        </SynapseDxStaffGate>
      </ProtectedPage>
    ),
  },
  // SynapseDx console catalog (SYN-1953, WS5): staff-shell guard + console flag.
  // DATA gate is the server grant (isPlatformAdmin, SYN-1939), not the Clerk flag.
  {
    path: "/synapsedx/catalog",
    element: (
      <ProtectedPage>
        <SynapseDxConsoleGate>
          <SynapseDxCatalog />
        </SynapseDxConsoleGate>
      </ProtectedPage>
    ),
  },

  // Agent management
  {
    path: "/agents",
    element: (
      <ProtectedPage>
        <MeAgents />
      </ProtectedPage>
    ),
  },
  {
    path: "/agents/new",
    element: (
      <ProtectedPage>
        <MeAgentEdit />
      </ProtectedPage>
    ),
  },
  {
    path: "/agents/:id",
    element: (
      <ProtectedPage>
        <MeAgentEdit />
      </ProtectedPage>
    ),
  },

  // Admin
  {
    path: "/admin",
    element: (
      <ProtectedPage>
        <AdminOverview />
      </ProtectedPage>
    ),
  },
  {
    path: "/admin/agents",
    element: (
      <ProtectedPage>
        <AdminAgents />
      </ProtectedPage>
    ),
  },
  {
    path: "/admin/agents/new",
    element: (
      <ProtectedPage>
        <AdminAgentEdit />
      </ProtectedPage>
    ),
  },
  {
    path: "/admin/agents/:id",
    element: (
      <ProtectedPage>
        <AdminAgentEdit />
      </ProtectedPage>
    ),
  },
  {
    path: "/admin/channels",
    element: (
      <ProtectedPage>
        <AdminChannels />
      </ProtectedPage>
    ),
  },
  {
    path: "/admin/models",
    element: (
      <ProtectedPage>
        <AdminModels />
      </ProtectedPage>
    ),
  },
  {
    path: "/admin/skills",
    element: (
      <ProtectedPage>
        <AdminSkills />
      </ProtectedPage>
    ),
  },
  {
    path: "/admin/roles",
    element: (
      <ProtectedPage>
        <AdminRoles />
      </ProtectedPage>
    ),
  },
  {
    path: "/admin/connectors",
    element: (
      <ProtectedPage>
        <AdminConnectors />
      </ProtectedPage>
    ),
  },
  {
    path: "/admin/usage",
    element: (
      <ProtectedPage>
        <AdminUsage />
      </ProtectedPage>
    ),
  },

  // Legacy redirects
  { path: "/catalog", element: <KeepSearchRedirect to="/customize/directory" /> },
  { path: "/me/agents", element: <Navigate to="/agents" replace /> },
  { path: "/command", element: <Navigate to="/chat" replace /> },
  { path: "/dashboard", element: <Navigate to="/chat" replace /> },
];

/**
 * Unified IA route table (flag ON, spec §6.2). Re-homes existing page content
 * under jobs-based paths and permanently redirects every old URL to its new
 * home so nothing 404s. Dedicated pages for the placeholders land later
 * (Home v1 = SYN-1505; Activity/Onboarding = wave-1).
 */
export const unifiedRoutes: RouteObject[] = [
  ...authRoutes,
  ...smbBuyingRoutes,

  // ⚠️ REGISTERED IN **BOTH** ROUTE TABLES, AND THAT IS THE TRAP.
  // `routesFor(unifiedShell)` picks between `legacyRoutes` and `unifiedRoutes` per org. A
  // route added to only one 404s for exactly the orgs on the other shell — and the org most
  // likely to be testing is the one on the new shell. Adding it to legacy alone is how slice
  // 5 shipped and immediately 404'd on 10 Aug.
  {
    path: "/documents/:id",
    element: (
      <AuthGuard>
        <DocumentView />
      </AuthGuard>
    ),
  },

  // ── Member ("you") surface ──────────────────────────────────────────────
  // Chat-first landing (no onboarding hard redirect): every signed-in user
  // lands in Chat, never gated on onboarding/provisioning status.
  { path: "/", element: <Navigate to="/chat" replace /> },
  {
    path: "/home",
    element: (
      <ProtectedPage>
        <Home />
      </ProtectedPage>
    ),
  },
  {
    path: "/publisher/export",
    element: (
      <ProtectedPage>
        <PublisherExport />
      </ProtectedPage>
    ),
  },
  {
    path: "/publisher/preview",
    element: (
      <ProtectedPage>
        <PublisherPreview />
      </ProtectedPage>
    ),
  },
  {
    path: "/chat",
    element: (
      <ProtectedPage>
        <Chat />
      </ProtectedPage>
    ),
  },
  {
    path: "/customize",
    element: (
      <ProtectedPage>
        <Customize />
      </ProtectedPage>
    ),
  },
  {
    path: "/customize/:section",
    element: (
      <ProtectedPage>
        <Customize />
      </ProtectedPage>
    ),
  },
  {
    path: "/customize/packages/:packageId",
    element: (
      <ProtectedPage>
        <Customize />
      </ProtectedPage>
    ),
  },
  // /customize/directory → /catalog (SYN-1948 AC3: workspace browse half redirects).
  // The directory listing is superseded by the unified catalog surface.
  {
    path: "/agents",
    element: (
      <ProtectedPage>
        <MeAgents />
      </ProtectedPage>
    ),
  },
  {
    path: "/agents/new",
    element: (
      <ProtectedPage>
        <MeAgentEdit />
      </ProtectedPage>
    ),
  },
  {
    path: "/agents/:id",
    element: (
      <ProtectedPage>
        <MeAgentEdit />
      </ProtectedPage>
    ),
  },
  // Unified Catalog (SYN-1948, WS2): four-facet surface (Contracts, Connections,
  // Skills, Agents) with Mine | Global Marketplace scope switch. Behind ui.unified_catalog;
  // contract-facet fallback (live useRoutines) so it ships before all BFF legs.
  // NO global rung here (that is the staff console, SYN-1953).
  {
    path: "/catalog",
    element: (
      <ProtectedPage>
        <Catalog />
      </ProtectedPage>
    ),
  },
  // Routines (SYN-1506, spec §7.3/§7.4): the list route /routines redirects to
  // /catalog ONLY when ui.unified_catalog is ON (SYN-1948 AC3). With the catalog
  // flag OFF it mounts the legacy Routines list — gated in RoutinesListOrRedirect
  // so flag-off is byte-identical. The detail and run-ceremony sub-paths stay so
  // deep links remain valid in both flag states.
  {
    path: "/routines",
    element: <RoutinesListOrRedirect />,
  },
  {
    path: "/routines/u/:useId",
    element: (
      <ProtectedPage>
        <PersonalRoutineDetail />
      </ProtectedPage>
    ),
  },
  {
    path: "/routines/:id",
    element: (
      <ProtectedPage>
        <RoutineDetail />
      </ProtectedPage>
    ),
  },
  // Contract-grade run ceremony (SYN-1512, spec §7.5): review, typed confirm /
  // second approval (rendered from Atlas policy state), evidence-pack result.
  {
    path: "/routines/:id/run",
    element: (
      <ProtectedPage>
        <RoutineRunCeremony />
      </ProtectedPage>
    ),
  },
  // Activity (SYN-1516, spec §7.6): run history + linkable run detail.
  {
    path: "/activity",
    element: (
      <ProtectedPage>
        <Activity />
      </ProtectedPage>
    ),
  },
  {
    path: "/activity/:id",
    element: (
      <ProtectedPage>
        <RunDetail />
      </ProtectedPage>
    ),
  },
  {
    path: "/settings",
    element: (
      <ProtectedPage>
        <UserSettings />
      </ProtectedPage>
    ),
  },
  {
    path: "/setup/claude-code",
    element: (
      <ProtectedPage>
        <SynapseDxStaffGate>
          <ClaudeCodeSetup />
        </SynapseDxStaffGate>
      </ProtectedPage>
    ),
  },
  // SynapseDx console catalog (SYN-1953, WS5): staff-shell guard + console flag.
  // DATA gate is the server grant (isPlatformAdmin, SYN-1939), not the Clerk flag.
  {
    path: "/synapsedx/catalog",
    element: (
      <ProtectedPage>
        <SynapseDxConsoleGate>
          <SynapseDxCatalog />
        </SynapseDxConsoleGate>
      </ProtectedPage>
    ),
  },
  // My Setup personal hub (SYN-1901): agents + sign-ins + profile in one page.
  // The nav entry lives in Layout (SYN-1898); the page resolves at /my-setup.
  {
    path: "/my-setup",
    element: (
      <ProtectedPage>
        <MySetup />
      </ProtectedPage>
    ),
  },
  // Compatibility redirect: a stray or bookmarked /my-setup/:section (the page
  // selects tabs via ?tab=, so the path section is not meaningful) lands on the
  // canonical /my-setup page rather than 404ing.
  { path: "/my-setup/:section", element: <KeepSearchRedirect to="/my-setup" /> },
  // Onboarding wizard (SYN-1507, spec §12) — reachable only by explicit
  // navigation (e.g. "Get started" links); nothing auto-redirects here.
  {
    path: "/onboarding",
    element: (
      <ProtectedPage>
        <Onboarding />
      </ProtectedPage>
    ),
  },

  // ── Workspace ("admin") surface ─────────────────────────────────────────
  { path: "/workspace", element: <Navigate to="/workspace/people" replace /> },
  // People (SYN-1509, spec §8.1): members table + invites with role pre-pick.
  {
    path: "/workspace/people",
    element: (
      <ProtectedPage>
        <People />
      </ProtectedPage>
    ),
  },
  // Roles workspace (SYN-1517, spec §8.2) — replaces the re-homed /admin/roles
  // two-panel page with the dedicated index + detail pages.
  {
    path: "/workspace/roles",
    element: (
      <ProtectedPage>
        <WorkspaceRolesIndex />
      </ProtectedPage>
    ),
  },
  {
    path: "/workspace/roles/:id",
    element: (
      <ProtectedPage>
        <WorkspaceRoleDetail />
      </ProtectedPage>
    ),
  },
  {
    path: "/workspace/skills",
    element: (
      <ProtectedPage>
        <WorkspaceSkills />
      </ProtectedPage>
    ),
  },
  // D11 M1: apps a publisher has offered to the org; its admin enables and disables them.
  {
    path: "/workspace/apps",
    element: (
      <ProtectedPage>
        <WorkspaceApps />
      </ProtectedPage>
    ),
  },
  // Connections (SYN-1520, spec §8.3): the dedicated page replaces the
  // re-homed /admin/connectors view; the Channels tab carries the old
  // /admin/channels content, whose URL keeps redirecting here.
  {
    path: "/workspace/connections",
    element: (
      <ProtectedPage>
        <WorkspaceConnections />
      </ProtectedPage>
    ),
  },
  // Database connections — SYN-2207, epic-005. Same routes as the legacy
  // table, from the one shared definition above.
  ...databaseConnectionRoutes,
  // AI Governance (SYN-1582): Command org-admin evidence console over the
  // Atlas-owned ACSS evidence BFF; read-only and fail-closed on Atlas errors.
  {
    path: "/workspace/governance",
    element: (
      <ProtectedPage>
        <WorkspaceGovernance />
      </ProtectedPage>
    ),
  },
  // Workspace agents re-homes /admin/agents (Models content folds in here as a
  // settings tab in the dedicated-page wave; old models URL redirects here).
  {
    path: "/workspace/agents",
    element: (
      <ProtectedPage>
        <AdminAgents />
      </ProtectedPage>
    ),
  },
  {
    path: "/workspace/agents/new",
    element: (
      <ProtectedPage>
        <AdminAgentEdit />
      </ProtectedPage>
    ),
  },
  {
    path: "/workspace/agents/:id",
    element: (
      <ProtectedPage>
        <AdminAgentEdit />
      </ProtectedPage>
    ),
  },
  // Billing re-homes /admin/usage, gated to org admins (members → /home).
  {
    path: "/workspace/billing",
    element: (
      <ProtectedPage>
        <BillingAdminGate>
          <AdminUsage />
        </BillingAdminGate>
      </ProtectedPage>
    ),
  },
  // Approvals (SYN-1521, spec §8.6): the unified admin inbox (access requests,
  // run confirmations, agent escalations).
  {
    path: "/workspace/approvals",
    element: (
      <ProtectedPage>
        <Approvals />
      </ProtectedPage>
    ),
  },

  // ── Permanent redirects: every old URL lands on its new home ─────────────
  // SYN-1948 WS2: /marketplace and the workspace browse half (/customize/directory)
  // collapse into /catalog ONLY when ui.unified_catalog is ON; with the catalog
  // flag OFF they fall back to /routines (their pre-catalog home), keeping flag-off
  // byte-identical. The /routines list route itself is mounted above and gated in
  // RoutinesListOrRedirect. /routines/:id and /routines/:id/run keep real page
  // routes above; not redirected.
  { path: "/marketplace", element: <CatalogBrowseRedirect /> },
  { path: "/customize/directory", element: <CatalogBrowseRedirect /> },
  { path: "/admin/skills", element: <KeepSearchRedirect to="/workspace/skills" /> },
  { path: "/admin", element: <KeepSearchRedirect to="/workspace" /> },
  { path: "/admin/roles", element: <KeepSearchRedirect to="/workspace/roles" /> },
  { path: "/admin/connectors", element: <KeepSearchRedirect to="/workspace/connections" /> },
  { path: "/admin/channels", element: <KeepSearchRedirect to="/workspace/connections" /> },
  { path: "/admin/agents", element: <KeepSearchRedirect to="/workspace/agents" /> },
  { path: "/admin/agents/new", element: <KeepSearchRedirect to="/workspace/agents/new" /> },
  { path: "/admin/agents/:id", element: <KeepSearchRedirect to="/workspace/agents/:id" /> },
  { path: "/admin/models", element: <KeepSearchRedirect to="/workspace/agents" /> },
  { path: "/admin/usage", element: <KeepSearchRedirect to="/workspace/billing" /> },
  // FinOps: admins → billing; members → home (handled by FinOpsRedirect).
  { path: "/finops", element: <FinOpsRedirect /> },
  // /run is an alias for /routines (SYN-1898 AC2): the nav item is "Run" at /routines.
  { path: "/run", element: <KeepSearchRedirect to="/routines" /> },
  { path: "/run/:id", element: <KeepSearchRedirect to="/routines/:id" /> },
  { path: "/run/:id/run", element: <KeepSearchRedirect to="/routines/:id/run" /> },
  // /my-setup is the canonical personal hub page (SYN-1901). The redirect to
  // /customize that existed before SYN-1901 has been removed; the real page
  // route above handles /my-setup directly.

  // Kept from the legacy table.
  { path: "/me/agents", element: <Navigate to="/agents" replace /> },
  { path: "/command", element: <Navigate to="/chat" replace /> },
  { path: "/dashboard", element: <Navigate to="/chat" replace /> },
];

/**
 * Default route table. Flag OFF (everyone, until an org is allowlisted) uses
 * the legacy IA — this alias keeps it byte-identical to the historical table.
 */
export const routes: RouteObject[] = legacyRoutes;

/** Select the IA route table for a unified-shell flag value. */
export function routesFor(unifiedShell: boolean): RouteObject[] {
  return unifiedShell ? unifiedRoutes : legacyRoutes;
}

/**
 * Mounts the router for the current org's IA. The `ui.unified_shell` flag is
 * org-scoped and re-evaluates on org switch (via OrgProvider), so the router is
 * rebuilt when it flips. Keying RouterProvider on the flag forces a clean
 * remount so in-flight navigations resolve against the active table.
 */
export function Routes() {
  const unifiedShell = useUnifiedShell();
  const router = useMemo(() => createBrowserRouter(routesFor(unifiedShell)), [unifiedShell]);
  return <RouterProvider key={unifiedShell ? "unified" : "legacy"} router={router} />;
}
